import { ref, computed } from 'vue'
import { useMediaQuery } from '@vueuse/core'

// Estado a nivel módulo (singleton): el evento beforeinstallprompt se dispara UNA vez
// y temprano, así que se captura globalmente vía initPwaInstall() desde main.js.
const deferredPrompt = ref(null)
const canInstall = ref(false)
const isInstalled = ref(false)

const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
// iPadOS reporta UA de Mac: se detecta por soporte táctil.
const isIos = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document)
const isAndroid = /android/i.test(ua)
const isMobile = isIos || isAndroid || /Mobi/i.test(ua)

function detectStandalone() {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
}

const isStandalone = ref(detectStandalone())

let initialized = false

export function initPwaInstall() {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  window.addEventListener('beforeinstallprompt', (e) => {
    // Evita el mini-infobar nativo: lo disparamos nosotros desde el botón del drawer.
    e.preventDefault()
    deferredPrompt.value = e
    canInstall.value = true
  })

  window.addEventListener('appinstalled', () => {
    isInstalled.value = true
    canInstall.value = false
    deferredPrompt.value = null
  })

  const mql = window.matchMedia?.('(display-mode: standalone)')
  mql?.addEventListener?.('change', (e) => {
    isStandalone.value = e.matches
  })
}

export function usePwaInstall() {
  // Doble gate: dispositivo mobile/tablet (user-agent) Y viewport ≤1024px (breakpoint lg de Tailwind).
  // Así un desktop queda oculto aunque emule UA mobile, y un tablet real (≤1024) sí lo muestra.
  const isMaxTablet = useMediaQuery('(max-width: 1024px)')
  const showInstallOption = computed(() =>
    isMobile && isMaxTablet.value && !isStandalone.value && !isInstalled.value
  )

  // Android/Chromium: hay prompt nativo disponible. iOS: nunca (requiere instrucciones manuales).
  const canPromptNative = computed(() => canInstall.value && !!deferredPrompt.value)

  async function promptInstall() {
    if (!deferredPrompt.value) return { outcome: 'unavailable' }
    deferredPrompt.value.prompt()
    const choice = await deferredPrompt.value.userChoice
    deferredPrompt.value = null
    canInstall.value = false
    return choice
  }

  return {
    isMobile,
    isIos,
    isAndroid,
    isStandalone,
    isInstalled,
    canInstall,
    canPromptNative,
    showInstallOption,
    promptInstall
  }
}
